import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  Autocomplete,
  useTheme,
  alpha,
  styled,
} from '@mui/material';
import { Building, X, Calendar, MinusCircle } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import userClientAccessService, { UserAccess } from '../services/api/userClientAccessService';
import { snowflakeApi } from '../utils/api';

interface UserAccessControlModalProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
  userName: string;
}

interface Client {
  client_id: number;
  name: string;
  client_number?: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: '600px',
    maxWidth: '800px',
  },
}));

const UserAccessControlModal: React.FC<UserAccessControlModalProps> = ({
  open,
  onClose,
  userEmail,
  userName,
}) => {
  const theme = useTheme();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [currentClients, setCurrentClients] = useState<UserAccess[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [clientsFetched, setClientsFetched] = useState(false);
  const [searchResults, setSearchResults] = useState<Client[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [processing, setProcessing] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      // Reset cache when modal closes so we fetch fresh data next time
      setAllClients([]);
      setClientsFetched(false);
      setSearchResults([]);
      setSearchInput('');
      setDebouncedSearch('');
      setSelectedClient(null);
    }
  }, [open, userEmail]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Perform search when debounced value changes
  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 2) {
      performSearch(debouncedSearch);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearch, currentClients, allClients, clientsFetched]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load clients user has access to
      const clientsResponse = await userClientAccessService.getUserClients(userEmail, true);
      setCurrentClients(clientsResponse.clients);
    } catch (error) {
      console.error('Error loading user access data:', error);
      showToast('Failed to load user access data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to filter search results based on current clients
  const filterSearchResults = useCallback((searchTerm: string, clients: Client[], currentClientsList: UserAccess[]) => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const searchLower = searchTerm.toLowerCase();
    return clients.filter((client) => {
      const name = client.name.toLowerCase();
      const clientNumber = client.client_number?.toLowerCase() || '';
      
      // Exclude clients user already has access to
      const hasAccess = currentClientsList.some(
        (access) => access.client_id === client.client_id
      );
      
      return !hasAccess && (name.includes(searchLower) || clientNumber.includes(searchLower));
    });
  }, []);

  const performSearch = useCallback(async (searchTerm: string) => {
    // Fetch all clients only once and cache them
    if (!clientsFetched) {
      setSearching(true);
      try {
        const response = await snowflakeApi.get('/api/v1/db/clients?limit=10000');
        
        // Response.data is an object with a 'clients' property
        const clientsData = response.data.clients || [];
        
        // Transform the response to match our Client interface
        const clients = clientsData.map((client: any) => ({
          client_id: client.client_id,
          name: client.name || '',
          client_number: client.client_number || ''
        }));
        
        setAllClients(clients);
        setClientsFetched(true);
        
        // Filter with newly fetched clients
        const filtered = filterSearchResults(searchTerm, clients, currentClients);
        setSearchResults(filtered);
      } catch (error) {
        console.error('Error fetching clients:', error);
        showToast('Failed to load clients', 'error');
      } finally {
        setSearching(false);
      }
    } else {
      // Filter cached results
      const filtered = filterSearchResults(searchTerm, allClients, currentClients);
      setSearchResults(filtered);
    }
  }, [clientsFetched, allClients, currentClients, filterSearchResults, showToast]);

  const handleAddClient = async (client: Client | null) => {
    if (!client) {
      return;
    }

    setProcessing(true);
    try {
      await userClientAccessService.grantAccess(
        userEmail,
        client.client_id,
        userName
      );
      showToast('Access granted successfully', 'success');
      setSelectedClient(null);
      setSearchInput('');
      
      // Reload current clients list - search results will update via useEffect
      const response = await userClientAccessService.getUserClients(userEmail);
      setCurrentClients(response.clients);
    } catch (error) {
      console.error('Error granting access:', error);
      showToast('Failed to grant access', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeAccess = async (clientId: number, clientName: string) => {
    if (!window.confirm(`Are you sure you want to revoke access for ${userName} to ${clientName}?`)) {
      return;
    }

    setProcessing(true);
    try {
      await userClientAccessService.revokeAccess(userEmail, clientId);
      showToast('Access revoked successfully', 'success');
      
      // Reload current clients list - search results will update via useEffect
      const response = await userClientAccessService.getUserClients(userEmail);
      setCurrentClients(response.clients);
    } catch (error) {
      console.error('Error revoking access:', error);
      showToast('Failed to revoke access', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '700px',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Building size={24} color={theme.palette.primary.main} />
            <Box>
              <Typography variant="h6" component="div">
                User Access Control
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Header with Search Field */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, pt: 1, flexShrink: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                Accessible Clients ({currentClients.length})
              </Typography>
              
              {/* Add Client Search Field */}
              <Box sx={{ flex: 1, maxWidth: 400 }}>
                <Autocomplete
                  value={selectedClient}
                  inputValue={searchInput}
                  onInputChange={(event, newInputValue) => {
                    setSearchInput(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    handleAddClient(newValue);
                  }}
                  options={searchResults}
                  getOptionLabel={(option) => {
                    return `${option.name} (${option.client_id})`;
                  }}
                  isOptionEqualToValue={(option, value) => option.client_id === value.client_id}
                  filterOptions={(x) => x}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    
                    return (
                      <Box key={option.client_id} component="li" {...otherProps}>
                        <Box>
                          <Typography variant="body1">
                            {option.name} ({option.client_id})
                          </Typography>
                          {option.client_number && (
                            <Typography variant="caption" color="text.secondary">
                              Client #: {option.client_number}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Add client..."
                      disabled={processing}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searching ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  loading={searching}
                  disabled={processing}
                  noOptionsText={searchInput.length < 2 ? "Type to search..." : "No clients found"}
                />
              </Box>
            </Box>

            {/* Assigned Clients List */}
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              {currentClients.length === 0 ? (
                <Alert severity="info">
                  This user doesn't have access to any clients yet. Use the search field to add clients.
                </Alert>
              ) : (
                <List
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    borderRadius: 2,
                  }}
                >
                  {currentClients.map((clientAccess, index) => {
                    const clientName = clientAccess.client_name || `Client ${clientAccess.client_id}`;
                    const grantedByName = clientAccess.granted_by_name || clientAccess.granted_by_email;
                    
                    return (
                      <React.Fragment key={clientAccess.access_id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body1" fontWeight={500}>
                                  {clientName}
                                </Typography>
                                {clientAccess.client_number && (
                                  <Typography variant="body2" color="text.secondary">
                                    ({clientAccess.client_number})
                                  </Typography>
                                )}
                                <Chip
                                  label="Active"
                                  size="small"
                                  color="success"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  mt: 0.5,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Calendar size={12} color={theme.palette.text.secondary} />
                                  <Typography variant="caption" color="text.secondary">
                                    Granted: {formatDate(clientAccess.granted_at)}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  By: {grantedByName}
                                </Typography>
                                {clientAccess.notes && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic' }}
                                  >
                                    • {clientAccess.notes}
                                  </Typography>
                                )}
                              </Box>
                            }
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div' }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleRevokeAccess(clientAccess.client_id, clientName)
                              }
                              disabled={processing}
                              sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <MinusCircle size={18} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          px: 3,
          py: 2,
          flexShrink: 0,
        }}
      >
        <Button onClick={onClose} disabled={processing}>
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default UserAccessControlModal;
